/**
 * iconTextNotation.js
 *
 * The two directions of the Designer's icon-in-text display convention,
 * extracted from PropertiesPanel.vue's `displayText`/`displayValueStr`
 * computeds (which were two identical inline copies) so any other string
 * editor can present icons the same way.
 *
 * The *stored* form of an icon inside a text property is the actual
 * Unicode character in the Private Use Area (U+E000–U+F8FF) — that is
 * what IconPicker.vue inserts, what the Konva renderer draws (see
 * iconFonts.js's processIconText), and what escapeCString() carries into
 * the exported jsonl. A PUA character is unreadable in a plain text
 * input, though, so every editable text field shows it as `\uXXXX`
 * instead and converts back on the way in.
 *
 * `\uXXXX` is not a second model: processIconText() and escapeCString()
 * both already accept it (escapeCString deliberately leaves `\u`
 * sequences alone), so a value that reaches the model still carrying
 * notation behaves identically. These helpers only decide what the user
 * looks at while typing.
 *
 * Pure string functions — no Vue, no Konva, no font loading.
 */

/** Private Use Area — where Material Icons / OpenHASP built-in icons live. */
const ICON_CHAR = /[\uE000-\uF8FF]/g;
const ICON_NOTATION = /\\u([0-9A-Fa-f]{4})/g;

/**
 * Stored form → editable form: PUA icon characters become `\uXXXX`.
 * Everything else is left byte-for-byte alone.
 *
 * @param {*} text
 * @returns {string}
 */
export function iconCharsToNotation(text) {
  if (text === undefined || text === null) return '';
  return String(text).replace(ICON_CHAR, char =>
    `\\u${char.charCodeAt(0).toString(16).toUpperCase().padStart(4, '0')}`);
}

/**
 * Editable form → stored form: `\uXXXX` becomes the character it names.
 * Case-insensitive on the hex digits, matching processIconText().
 *
 * @param {*} text
 * @returns {string}
 */
export function iconNotationToChars(text) {
  if (text === undefined || text === null) return '';
  return String(text).replace(ICON_NOTATION, (_match, code) =>
    String.fromCharCode(parseInt(code, 16)));
}
